import { useEffect, useMemo, useState } from "react";
import { findByGuestId, type Attendee } from "../lib/attendees";
import {
  buildDiscoverScene,
  isPickleSceneDebugEnabled,
  logDiscoverScene,
} from "../lib/discoverScene";
import {
  getCursor,
  loadOrBuildDeck,
  ROUND_SIZE,
  setCursor,
} from "../lib/deck";
import { AttendeeCard } from "./AttendeeCard";
import { Avatar } from "./Avatar";

type DiscoverProps = {
  me: Attendee;
  attendees: readonly Attendee[];
};

export const Discover = ({ me, attendees }: DiscoverProps) => {
  const allIds = useMemo(() => attendees.map((a) => a.guestId), [attendees]);

  const [deck] = useState<string[]>(() => loadOrBuildDeck(me.guestId, allIds));
  const [cursor, setCursorState] = useState<number>(() => getCursor());
  const [roundPos, setRoundPos] = useState<number>(0);
  const [showGrid, setShowGrid] = useState<boolean>(false);

  const totalLeft = deck.length - cursor;
  const allDone = totalLeft <= 0;
  const showInterstitial = !allDone && roundPos >= ROUND_SIZE;

  const currentAttendee = useMemo<Attendee | null>(() => {
    if (allDone || showInterstitial) return null;
    const id = deck[cursor];
    return id ? findByGuestId(id, attendees) : null;
  }, [deck, cursor, allDone, showInterstitial, attendees]);

  const scene = useMemo(
    () =>
      buildDiscoverScene({
        deck,
        cursor,
        roundPos,
        showGrid,
        currentGuestId: currentAttendee?.guestId ?? null,
        currentLabel:
          currentAttendee?.firstName || currentAttendee?.fullName || null,
      }),
    [
      deck,
      cursor,
      roundPos,
      showGrid,
      currentAttendee?.guestId,
      currentAttendee?.firstName,
      currentAttendee?.fullName,
    ],
  );

  useEffect(() => {
    if (!isPickleSceneDebugEnabled()) return;
    window.__pickleDiscoverScene = scene;
    window.logPickleScene = () => {
      logDiscoverScene(scene, "update");
      return scene;
    };
    logDiscoverScene(scene, "update");
  }, [scene]);

  const advance = () => {
    const nextCursor = cursor + 1;
    setCursor(nextCursor);
    setCursorState(nextCursor);
    setRoundPos((p) => p + 1);
  };

  const startNextRound = () => {
    setRoundPos(0);
  };

  const jumpToAttendee = (guestId: string) => {
    const idx = deck.indexOf(guestId);
    if (idx < 0) return;
    setCursor(idx);
    setCursorState(idx);
    setRoundPos(0);
    setShowGrid(false);
  };

  if (showGrid) {
    return (
      <EveryoneGrid
        deck={deck}
        cursor={cursor}
        attendees={attendees}
        onPick={jumpToAttendee}
        onBack={() => setShowGrid(false)}
      />
    );
  }

  if (allDone) {
    return <EveryoneMet onSeeEveryone={() => setShowGrid(true)} />;
  }

  if (showInterstitial) {
    const moreLeft = Math.min(ROUND_SIZE, totalLeft);
    return (
      <Interstitial
        moreLeft={moreLeft}
        onContinue={startNextRound}
        onSeeEveryone={() => setShowGrid(true)}
      />
    );
  }

  if (!currentAttendee) {
    return <EveryoneMet onSeeEveryone={() => setShowGrid(true)} />;
  }

  const isLastInRound = roundPos === ROUND_SIZE - 1;
  const isLastEver = totalLeft === 1;
  const nextLabel = isLastEver
    ? "Finish"
    : isLastInRound
      ? "Finish round →"
      : "Next →";

  return (
    <div className="mx-auto w-full max-w-md px-6 py-8">
      <div className="mb-5 flex items-center justify-between text-xs text-slate-500">
        <span>
          {roundPos + 1} <span className="text-slate-700">/</span> {ROUND_SIZE}
        </span>
        <span>{totalLeft} left to meet</span>
      </div>

      <AttendeeCard attendee={currentAttendee} />

      <div className="mt-6 flex justify-end">
        <button
          type="button"
          onClick={advance}
          className="rounded-xl bg-emerald-500 px-5 py-2.5 font-medium text-slate-950 transition hover:bg-emerald-400 active:scale-[0.98]"
        >
          {nextLabel}
        </button>
      </div>

      <div className="mt-8 text-center">
        <button
          type="button"
          onClick={() => setShowGrid(true)}
          className="text-xs text-slate-500 underline decoration-slate-700 underline-offset-4 transition hover:text-slate-300 hover:decoration-slate-500"
        >
          See everyone
        </button>
      </div>
    </div>
  );
};

const Interstitial = ({
  moreLeft,
  onContinue,
  onSeeEveryone,
}: {
  moreLeft: number;
  onContinue: () => void;
  onSeeEveryone: () => void;
}) => (
  <div className="mx-auto w-full max-w-md px-6 py-16 text-center">
    <div className="text-6xl">🎉</div>
    <h2 className="mt-4 text-3xl font-semibold tracking-tight">
      You've met 5 new humans.
    </h2>
    <p className="mt-3 text-slate-400">
      Now go say hi to one of them. Or keep going — {moreLeft} more await.
    </p>
    <button
      type="button"
      onClick={onContinue}
      className="mt-8 rounded-xl bg-emerald-500 px-6 py-3 font-medium text-slate-950 transition hover:bg-emerald-400 active:scale-[0.98]"
    >
      See {moreLeft} more →
    </button>
    <div className="mt-6">
      <button
        type="button"
        onClick={onSeeEveryone}
        className="text-xs text-slate-500 underline decoration-slate-700 underline-offset-4 transition hover:text-slate-300 hover:decoration-slate-500"
      >
        See everyone
      </button>
    </div>
  </div>
);

const EveryoneMet = ({ onSeeEveryone }: { onSeeEveryone: () => void }) => (
  <div className="mx-auto w-full max-w-md px-6 py-16 text-center">
    <div className="text-6xl">🥒</div>
    <h2 className="mt-4 text-3xl font-semibold tracking-tight">
      You've met everyone.
    </h2>
    <p className="mt-3 text-slate-400">
      That's the whole room. Go grab a taco and start a conversation.
    </p>
    <div className="mt-8">
      <button
        type="button"
        onClick={onSeeEveryone}
        className="rounded-xl border border-slate-700 px-5 py-2.5 text-sm text-slate-200 transition hover:border-slate-500"
      >
        See everyone
      </button>
    </div>
  </div>
);

const EveryoneGrid = ({
  deck,
  cursor,
  attendees,
  onPick,
  onBack,
}: {
  deck: readonly string[];
  cursor: number;
  attendees: readonly Attendee[];
  onPick: (guestId: string) => void;
  onBack: () => void;
}) => {
  const seen = new Set(deck.slice(0, cursor));
  const list = deck
    .map((id) => findByGuestId(id, attendees))
    .filter((a): a is Attendee => a !== null);

  return (
    <div className="mx-auto w-full max-w-3xl px-4 py-8">
      <div className="mb-6 flex items-center justify-between">
        <button
          type="button"
          onClick={onBack}
          className="text-sm text-slate-400 transition hover:text-slate-100"
        >
          ← Back
        </button>
        <p className="text-xs uppercase tracking-wider text-slate-500">
          Everyone ({list.length})
        </p>
        <span className="w-8" />
      </div>

      <p className="mb-6 text-center text-sm text-slate-400">
        Tap anyone to jump to their card.
      </p>

      <ul className="grid grid-cols-3 gap-x-3 gap-y-6 sm:grid-cols-5">
        {list.map((a) => {
          const isSeen = seen.has(a.guestId);
          return (
            <li key={a.guestId}>
              <button
                type="button"
                onClick={() => onPick(a.guestId)}
                className="group flex w-full flex-col items-center text-center"
              >
                <span
                  className={`transition group-hover:scale-105 ${isSeen ? "opacity-40" : ""}`}
                >
                  <Avatar attendee={a} size={72} />
                </span>
                <span
                  className={`mt-2 truncate text-xs ${isSeen ? "text-slate-500" : "text-slate-200"}`}
                  title={a.fullName}
                >
                  {a.firstName || a.fullName}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
};
